/**
 * The Overview's widgets on the Apps tab: Performance (the runner's CPU and memory of the service now, a bar against
 * the plan's limit only when the plan has one), Suggestions (serviceSuggestionsModel), Technologies (serviceStackModel)
 * and, for a Docker Compose app, the compose file's services. Each shows only what it was given.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { formatBytes, formatCores, getSandbox, isRunning } from '../../../services/SandboxService';
import { parseCompose } from '../../../utils/composeModel';
import { Card, useLoaded } from './common';

const hasNumber = (value) => value !== null && value !== undefined && !Number.isNaN(Number(value));

function Meter({ label, value, used, limit, limitText, testId }) {
  const share = hasNumber(limit) && Number(limit) > 0 && hasNumber(used) ? Math.min(Number(used) / Number(limit), 1) : null;
  return (
    <div className="apps-meter" data-testid={testId}>
      <div className="apps-meter-head"><span className="apps-muted">{label}</span><strong>{value}</strong></div>
      {share !== null && (
        <>
          <div aria-hidden="true" className="apps-bar"><div className={`apps-bar-fill${share > 0.85 ? ' is-high' : ''}`} style={{ width: `${Math.round(share * 100)}%` }} /></div>
          <span className="apps-muted apps-small">of {limitText} in your plan</span>
        </>
      )}
    </div>
  );
}
Meter.propTypes = { label: PropTypes.string, limit: PropTypes.any, limitText: PropTypes.string, testId: PropTypes.string, used: PropTypes.any, value: PropTypes.string };

export function PerformanceCard({ runner = null }) {
  const plan = useLoaded(() => getSandbox(), []);
  const limits = plan.value?.plan?.limits || {};
  const running = isRunning(runner);
  const measured = hasNumber(runner?.cpuPercent) || hasNumber(runner?.memoryBytes);
  return (
    <Card testId="apps-info-performance" title="Performance now">
      {!running ? <p className="apps-muted">Not running: nothing to measure.</p> : !measured ? <p className="apps-muted">No measurement yet.</p> : (
        <div className="apps-meters">
          {hasNumber(runner.cpuPercent) && <Meter label="CPU" limit={limits.cpu} limitText={`${limits.cpu} vCPU`} testId="apps-meter-cpu" used={Number(runner.cpuPercent) / 100} value={formatCores(runner.cpuPercent)} />}
          {hasNumber(runner.memoryBytes) && <Meter label="Memory" limit={limits.memoryBytes} limitText={formatBytes(limits.memoryBytes)} testId="apps-meter-memory" used={runner.memoryBytes} value={formatBytes(runner.memoryBytes)} />}
        </div>
      )}
    </Card>
  );
}
PerformanceCard.propTypes = { runner: PropTypes.object };

const ACTION_LABELS = { logs: 'Read the log', config: 'Config', quality: 'Quality', api: 'API' };

// `actions`: { logs|config|quality|api: () => void }, the ones this page can do (a hidden tab has none).
export function SuggestionsCard({ items, actions = {}, loading = false }) {
  return (
    <Card testId="apps-info-suggestions" title="Suggestions">
      {items.length === 0 ? <p className="apps-muted">{loading ? 'Loading…' : 'Nothing to suggest: it looks in order.'}</p> : (
        <ul className="apps-suggestions">
          {items.map((item) => (
            <li data-testid={`apps-suggestion-${item.id}`} key={item.id}>
              <span>{item.text}</span>
              {ACTION_LABELS[item.action] && actions[item.action] && (
                <button className="apps-info-link" onClick={actions[item.action]} type="button">{ACTION_LABELS[item.action]}</button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
SuggestionsCard.propTypes = { actions: PropTypes.object, items: PropTypes.array.isRequired, loading: PropTypes.bool };

export function TechnologiesCard({ list, loading = false }) {
  return (
    <Card testId="apps-info-tech" title="Technologies">
      {list.length === 0 ? <p className="apps-muted">{loading ? 'Loading…' : 'Nothing recognised in its folder.'}</p> : (
        <div className="apps-chips is-tight">{list.map((name) => <span className="apps-tag" key={name}>{name}</span>)}</div>
      )}
    </Card>
  );
}
TechnologiesCard.propTypes = { list: PropTypes.arrayOf(PropTypes.string).isRequired, loading: PropTypes.bool };

/** The compose file's services: name, image or "built here", ports. Their own state is not reported by the runner. */
export function ComposeCard({ file, text }) {
  const services = parseCompose(text);
  return (
    <Card testId="apps-info-compose" title={`Compose services · ${file}`} wide>
      {services.length === 0 ? <p className="apps-muted">{text ? 'No services found in the file.' : 'The file could not be read.'}</p> : (
        <ul className="apps-rows">
          {services.map((service) => (
            <li data-testid={`apps-compose-${service.name}`} key={service.name}>
              <span className="apps-row-name">{service.name} <span className="apps-muted apps-small">{service.build ? 'built from this folder' : service.image || ''}</span></span>
              <span className="apps-muted">{service.ports.length ? service.ports.join(', ') : 'no published port'}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
ComposeCard.propTypes = { file: PropTypes.string.isRequired, text: PropTypes.string };
